import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type ApprovalRequestView } from '../../api/client.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

/** Messaging → Approvals (BB-106): requests waiting for a reviewer, and earlier decisions. */
export function ApprovalInboxPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/approvals' });
  const { api } = useRouteContext({ from: '/env/$environmentId/approvals' });
  const [status, setStatus] = useState<ApprovalRequestView['status'] | 'all'>('pending');
  const list = useQuery({ queryKey: ['approvals', environmentId, status], queryFn: () => api.approvalRequests(environmentId, status) });
  return (
    <section>
      <h1>Approvals</h1>
      <p className="hint">Revisions waiting for a reviewer. Any change to a draft invalidates its request.</p>
      <div className="filters">
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="pending">Waiting for review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="invalidated">Invalidated</option>
          <option value="all">All</option>
        </select>
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading approvals…</p>
      ) : list.isError ? (
        <ErrorState title="Approvals could not be loaded" message={list.error instanceof ApiError ? list.error.message : 'Something went wrong.'} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={status === 'pending' ? 'Nothing to review' : 'No requests'} />
      ) : (
        <table className="table" aria-label="Approval requests">
          <thead>
            <tr>
              <th scope="col">Campaign</th>
              <th scope="col">Revision</th>
              <th scope="col">Asked by</th>
              <th scope="col">Rules</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: r.objectId }}>
                    {r.objectName}
                  </Link>
                </td>
                <td>{r.revision}</td>
                <td>
                  {r.requestedBy?.email ?? '—'}, {formatWhen(r.requestedAt)}
                </td>
                <td>{r.triggeredRules.map((x) => x.reason).join('; ') || '—'}</td>
                <td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
